import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { ethers } from 'ethers';
import {
  Card, CardHeader, Label, Input, Button, Alert, LoadingSpinner, HealthBar
} from './StyledComponents';
import { useEasyVault } from '../hooks/useEasyVault';
import { formatNumber, formatCurrency } from '../utils/formatters';
import { borrowPlan, numberToWad, wadToNumber, debtFloorViolation, liquidationPriceWad, RAY } from '../utils/vaultMath';

const toWad = (v) => ethers.parseEther(Number(v).toFixed(18));

// ---------------------------------------------------------------------------
// Easy Vault: one transaction per operation, powered by MakerDAO's audited proxy
// stack over the WPLS-A ilk. Layout: a position summary with meaning-first
// explainers, then one aligned section per action.
// ---------------------------------------------------------------------------

const PositionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 1rem;
`;

const Stat = styled.div`
  background: rgba(16, 16, 16, 0.5);
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 1rem;

  .label {
    font-size: 0.75rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .value {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0.3rem 0 0.35rem;
    color: ${(p) => (p.$tone === 'danger' ? '#ef4444' : p.$tone === 'warning' ? '#f59e0b' : p.$tone === 'success' ? '#10b981' : '#ffd700')};
  }
  .meaning {
    font-size: 0.75rem;
    color: #999;
    line-height: 1.45;
  }
`;

const Section = styled.div`
  border-top: 1px solid #2a2a2a;
  margin-top: 1.75rem;
  padding-top: 1.25rem;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: #ffd700;
  }
  .explain {
    margin: 0.3rem 0 1rem;
    font-size: 0.85rem;
    color: #999;
    line-height: 1.5;
  }
`;

const ControlRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.75rem;
  margin-top: 0.9rem;
`;

const SafetyRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin: 0.9rem 0;

  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Hint = styled.div`
  font-size: 0.78rem;
  color: #888;
  margin-top: 0.4rem;
`;

// Parallel-alternative cards: three titled boxes with identical button styling,
// so nothing reads as "step 1 of 3" or as disabled. (A gold primary next to two
// outline secondaries read as a required sequence — it isn't one.)
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
`;

const Choice = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(16, 16, 16, 0.5);
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 1rem;

  h4 {
    margin: 0 0 0.35rem;
    color: #fff;
    font-size: 0.95rem;
  }
  p {
    margin: 0 0 0.9rem;
    font-size: 0.8rem;
    color: #999;
    line-height: 1.5;
    flex: 1;
  }
`;

const EasyVault = ({ signer, account, balances, systemData, onRefresh }) => {
  const ev = useEasyVault(signer, account, onRefresh);
  const [busy, setBusy] = useState('');
  const [borrowAmt, setBorrowAmt] = useState('');
  const [safetyPct, setSafetyPct] = useState(200);
  const [repayAmt, setRepayAmt] = useState('');
  const [collAmt, setCollAmt] = useState('');

  const GAS_RESERVE = 2500; // PLS kept back so future transactions stay payable

  const run = async (label, fn) => {
    setBusy(label);
    toast.loading(`${label} — confirm in your wallet, then wait for the chain`, { id: 'easy' });
    try {
      await fn();
      toast.success(`${label} — done!`, { id: 'easy' });
      return true;
    } catch (e) {
      console.error(`${label} failed:`, e);
      toast.error(`${label} failed: ${e?.reason || e?.shortMessage || e?.message || 'unknown error'}`, { id: 'easy' });
      return false;
    } finally {
      setBusy('');
    }
  };

  // --- borrow plan (same audited math as the classic goal-seek) ---
  const mat = ev.rawIlk ? ev.rawIlk.mat : null;
  const matN = mat ? Number(mat) / 1e27 : 1.5;
  const plan = ev.rawIlk && borrowAmt && parseFloat(borrowAmt) > 0
    ? borrowPlan(toWad(borrowAmt), numberToWad(safetyPct / 100), (mat * 10n ** 18n) / RAY, ev.rawIlk)
    : null;
  const depositNum = plan ? wadToNumber(plan.additionalInkWad) : 0;
  const lockPrice = plan
    ? wadToNumber(liquidationPriceWad(ev.rawIlk.ink + plan.additionalInkWad, plan.newDebtWad,
        (ev.rawIlk.par * 10n ** 18n) / RAY, mat))
    : 0;
  const walletPls = parseFloat(balances.pls || '0');
  const shortOnPls = plan && depositNum > 0 && depositNum + GAS_RESERVE > walletPls;

  const maxBorrow = () => {
    if (!ev.rawIlk) return 0;
    const walletAvail = Math.max(0, walletPls - GAS_RESERVE);
    const inkTotal = wadToNumber(ev.rawIlk.ink) + walletAvail;
    const spotNum = Number(ev.rawIlk.spot) / 1e27;
    const capacity = ((inkTotal * spotNum * matN) / (safetyPct / 100)) / 1.001;
    return Math.max(0, Math.floor(Math.min(capacity - parseFloat(ev.debt), parseFloat(ev.availableDebt))));
  };

  const doBorrow = async () => {
    const floorMsg = debtFloorViolation('mint-jdai', borrowAmt, ev.debt, ev.minDebt);
    if (floorMsg) { toast.error(floorMsg); return; }
    if (parseFloat(borrowAmt) > parseFloat(ev.availableDebt)) {
      toast.error(`Only ${formatNumber(ev.availableDebt, 0)} JDAI of ceiling room is left.`); return;
    }
    if (!plan) { toast.error('Still loading vault data — one moment.'); return; }
    if (shortOnPls) { toast.error('Not enough PLS in your wallet for this plan (incl. gas reserve).'); return; }
    const ok = await run(`Borrowing ${formatNumber(borrowAmt, 2)} JDAI`,
      () => ev.borrow(plan.additionalInkWad, toWad(borrowAmt)));
    if (ok) setBorrowAmt('');
  };

  const doRepay = async (all) => {
    if (!all) {
      const floorMsg = debtFloorViolation('repay-jdai', repayAmt, ev.debt, ev.minDebt);
      if (floorMsg) { toast.error(floorMsg); return; }
    }
    const walletJdai = parseFloat(balances.jdai || '0');
    const needed = all ? parseFloat(ev.debt) : parseFloat(repayAmt);
    if (needed > walletJdai + 1e-6) {
      toast.error(`You need ${formatNumber(needed, 4)} JDAI in your wallet (you have ${formatNumber(walletJdai, 4)}).`); return;
    }
    const ok = all
      ? await run('Repaying all debt', () => ev.repayAll())
      : await run(`Repaying ${formatNumber(repayAmt, 2)} JDAI`, () => ev.repay(toWad(repayAmt)));
    if (ok) setRepayAmt('');
  };

  const maxSafeWithdraw = () => {
    const coll = parseFloat(ev.collateral), debt = parseFloat(ev.debt);
    if (debt === 0) return coll;
    const price = parseFloat(systemData.oraclePlsPrice || '0');
    const target = parseFloat(systemData.oracleJdaiTarget || '0');
    if (!(price > 0)) return 0;
    return Math.max(0, coll - (debt * target * 1.6) / price); // 160% buffer above the 150% line
  };

  // ---------------------------------------------------------------------------
  // What this position costs, and what a price drop would do to it. Both are
  // derived from figures already loaded — no extra chain reads.
  const feePct = parseFloat(systemData.stabilityFee || '0');
  const debtNum = parseFloat(ev.debt || '0');
  const jdaiUsd = parseFloat(systemData.oracleJdaiTarget || systemData.jdaiPrice || '0');
  const annualCostJdai = debtNum * (feePct / 100);
  const annualCostUsd = annualCostJdai * jdaiUsd;
  // health scales linearly with collateral price, so a drop of d gives h*(1-d)
  const dropScenarios = [10, 25, 50].map((d) => ({
    drop: d,
    health: ev.healthRatio === Infinity ? Infinity : ev.healthRatio * (1 - d / 100)
  }));

  if (ev.isLoading) {
    return <Card style={{ gridColumn: '1 / -1' }}><CardHeader><h2>Your Vault</h2></CardHeader><LoadingSpinner /> Loading from the chain…</Card>;
  }

  // Step 0: one-time proxy build
  if (!ev.proxy) {
    return (
      <Card style={{ gridColumn: '1 / -1' }}>
        <CardHeader><h2>One-time setup</h2></CardHeader>
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          Your vault runs through a small personal contract called a <strong>proxy</strong>.
          You own it, only you can use it, and you create it once — after this, every
          vault action is a single transaction.
        </p>
        <Button onClick={() => run('Creating your proxy', ev.buildProxy)} disabled={!!busy} style={{ marginTop: '1rem' }}>
          {busy ? <LoadingSpinner /> : 'Create my proxy (one-time)'}
        </Button>
      </Card>
    );
  }

  const hasPosition = ev.cdpId && parseFloat(ev.collateral) > 0;
  const hasDebt = parseFloat(ev.debt) > 0;
  const healthTone = ev.healthRatio === Infinity || ev.healthRatio >= 1.3 ? 'success'
    : ev.healthRatio >= 1 ? 'warning' : 'danger';

  return (
    <>
      {/* Left column: dashboard */}
      <Card>
        <CardHeader>
          <div>
            <h2>Your Vault {ev.cdpId ? `#${ev.cdpId}` : ''}</h2>
            <p>Deposit PLS, borrow JDAI, repay any time to unlock your PLS.</p>
          </div>
        </CardHeader>

      {!hasPosition && (
        <Alert variant="info">
          <div className="title">No vault open yet</div>
          <div className="message">
            Pick the JDAI you want and a safety level below — one transaction opens the
            vault, locks your PLS, and puts the JDAI in your wallet.
          </div>
        </Alert>
      )}
      {hasPosition && (
        <>
          <PositionGrid>
            <Stat>
              <div className="label">Collateral</div>
              <div className="value">{formatNumber(ev.collateral, 0)} PLS</div>
              <div className="meaning">Locked to back your loan. Returned in full when the debt is repaid.</div>
            </Stat>
            <Stat>
              <div className="label">Debt</div>
              <div className="value">{formatNumber(ev.debt, 2)} JDAI</div>
              <div className="meaning">What you repay to unlock the collateral. No schedule, no deadline.</div>
            </Stat>
            <Stat $tone={healthTone}>
              <div className="label">Safety</div>
              <div className="value">{ev.healthRatio === Infinity ? '∞' : `${formatNumber(ev.healthRatio, 2)}×`}</div>
              <div className="meaning">1.00× is the limit. Below it you can&apos;t borrow or withdraw — and once liquidations activate (announced in advance), a vault under the line can be liquidated with a penalty.</div>
            </Stat>
            <Stat>
              <div className="label">Locks below</div>
              <div className="value">{hasDebt ? formatCurrency(ev.liquidationPrice, 8) : '—'}</div>
              <div className="meaning">The PLS price at which safety hits 1.00×. Repaying debt or adding collateral moves it lower.</div>
            </Stat>
            <Stat>
              <div className="label">Cost to hold</div>
              <div className="value">{hasDebt ? `${formatNumber(annualCostJdai, 3)} JDAI/yr` : '—'}</div>
              <div className="meaning">
                {hasDebt
                  ? `The ${feePct}% stability fee on your debt — about ${formatCurrency(annualCostUsd, 2)} a year. It accrues continuously; there is nothing to pay on a schedule.`
                  : 'The stability fee only applies to borrowed JDAI.'}
              </div>
            </Stat>
          </PositionGrid>
          {hasDebt && (
            <div style={{ marginTop: '0.9rem' }}>
              <HealthBar ratio={ev.healthRatio}><div className="fill" /></HealthBar>
              <div style={{ marginTop: '0.9rem', fontSize: '0.82rem', color: '#999' }}>
                <strong style={{ color: '#ccc' }}>If PLS falls:</strong>{' '}
                {dropScenarios.map((sc, idx) => (
                  <span key={sc.drop}>
                    {idx > 0 && <span style={{ color: '#444' }}> · </span>}
                    −{sc.drop}% →{' '}
                    <span style={{ color: sc.health < 1 ? '#ef4444' : sc.health < 1.3 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                      {sc.health === Infinity ? '∞' : `${formatNumber(sc.health, 2)}×`}
                      {sc.health < 1 ? ' liquidatable' : ''}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </Card>

      {/* Right column: actions */}
      <Card>
        <CardHeader>
          <div>
            <h2>Manage</h2>
            <p>Every action here is a single transaction.</p>
          </div>
        </CardHeader>

      {/* ------------------------------- Borrow ------------------------------- */}
      <Section style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
        <h3>{hasPosition ? 'Borrow more' : 'Open your vault'}</h3>
        <p className="explain">
          Choose the JDAI you want and a safety cushion. We compute the PLS deposit,
          then one transaction locks it and puts the JDAI in your wallet.
        </p>
        <Label>Amount of JDAI</Label>
        <ControlRow style={{ marginTop: '0.4rem' }}>
          <Input type="number" value={borrowAmt} onChange={(e) => setBorrowAmt(e.target.value)}
            placeholder="10" step="1" min="0" />
          <Button type="button" variant="secondary" onClick={() => setBorrowAmt(String(maxBorrow()))}>
            Max
          </Button>
        </ControlRow>
        <Hint>
          Minimum {formatNumber(ev.minDebt, 0)} JDAI · system has room for {formatNumber(ev.availableDebt, 0)} more
        </Hint>

        <SafetyRow>
          {[170, 200, 300].map((pct) => (
            <Button key={pct} type="button" variant={safetyPct === pct ? undefined : 'secondary'}
              onClick={() => setSafetyPct(pct)} style={{ padding: '0.7rem 0.5rem' }}>
              {pct}%
              <div style={{ fontSize: '0.72rem', fontWeight: 'normal', marginTop: '0.15rem' }}>
                {pct === 170 ? 'tight' : pct === 200 ? 'balanced' : 'conservative'} · survives a ~{Math.round((1 - matN / (pct / 100)) * 100)}% price drop
              </div>
            </Button>
          ))}
        </SafetyRow>

        {plan && (
          <Alert variant="info" style={{ marginBottom: '0.9rem' }}>
            <div className="message">
              Deposit <strong style={{ color: '#ffd700' }}>{formatNumber(depositNum, 0)} PLS</strong>
              {depositNum === 0 ? ' (your current collateral already covers it)' : ''} → receive{' '}
              <strong style={{ color: '#ffd700' }}>{formatNumber(borrowAmt, 2)} JDAI</strong>, one transaction.
              <div style={{ marginTop: '0.4rem', fontSize: '0.85rem' }}>
                After this, your vault locks only if PLS falls below ≈ ${lockPrice.toFixed(9)}.
              </div>
              {shortOnPls && (
                <div style={{ color: '#ef4444', marginTop: '0.4rem' }}>
                  ⚠️ Wallet holds {formatNumber(walletPls, 0)} PLS — this needs {formatNumber(depositNum, 0)} plus
                  ~{formatNumber(GAS_RESERVE, 0)} kept for gas. Lower the amount or the safety level.
                </div>
              )}
            </div>
          </Alert>
        )}
        <ButtonRow>
          <Button onClick={doBorrow} disabled={!!busy || !borrowAmt || parseFloat(borrowAmt) <= 0 || !ev.rawIlk}>
            {busy.startsWith('Borrowing') ? <LoadingSpinner /> : hasPosition ? 'Borrow' : 'Open vault & borrow'}
          </Button>
        </ButtonRow>
      </Section>

      {/* -------------------------------- Repay ------------------------------- */}
      {hasDebt && (
        <Section>
          <h3>Repay</h3>
          <p className="explain">
            Return JDAI to reduce or clear your debt. Nothing here is a sequence —
            each option below stands on its own.
          </p>
          {!ev.allowanceOk ? (
            <>
              <Alert variant="info" style={{ marginBottom: '0.9rem' }}>
                <div className="message">
                  One-time approval needed: this lets your own proxy — nobody else — use
                  your JDAI for repayments. You won&apos;t be asked again.
                </div>
              </Alert>
              <ButtonRow>
                <Button variant="secondary" onClick={() => run('Approving JDAI', ev.approveRepay)} disabled={!!busy}>
                  {busy === 'Approving JDAI' ? <LoadingSpinner /> : 'Approve JDAI (one-time)'}
                </Button>
              </ButtonRow>
            </>
          ) : (
            <>
              <Hint style={{ margin: '0 0 0.75rem' }}>
                You owe {formatNumber(ev.debt, 4)} JDAI · wallet holds {formatNumber(balances.jdai, 4)} JDAI.
                These are three alternatives — pick one, in any order.
              </Hint>
              <ChoiceGrid>
                <Choice>
                  <h4>Repay part of it</h4>
                  <p>Lower the debt, keep the vault open. Must leave at least {formatNumber(ev.minDebt, 0)} JDAI of debt.</p>
                  <ControlRow style={{ marginBottom: '0.6rem' }}>
                    <Input type="number" value={repayAmt} onChange={(e) => setRepayAmt(e.target.value)}
                      placeholder="0.00" step="0.01" min="0" />
                  </ControlRow>
                  <Button onClick={() => doRepay(false)} disabled={!!busy || !repayAmt || parseFloat(repayAmt) <= 0}>
                    Repay amount
                  </Button>
                </Choice>
                <Choice>
                  <h4>Repay all of it</h4>
                  <p>Clear the whole debt. Your PLS stays locked, ready to borrow against again.</p>
                  <Button onClick={() => doRepay(true)} disabled={!!busy}>
                    Repay all ({formatNumber(ev.debt, 2)} JDAI)
                  </Button>
                </Choice>
                <Choice>
                  <h4>Close the vault</h4>
                  <p>Clear the debt and get every PLS back in your wallet. One transaction.</p>
                  <Button disabled={!!busy} onClick={() => run('Closing vault', ev.closeVault)}>
                    Close &amp; withdraw all
                  </Button>
                </Choice>
              </ChoiceGrid>
            </>
          )}
        </Section>
      )}

      {/* ---------------------------- Collateral ------------------------------ */}
      {hasPosition && (
        <Section>
          <h3>Collateral</h3>
          <p className="explain">
            Add PLS to raise your safety margin, or withdraw PLS you don&apos;t need for
            backing. Withdrawals are capped so the vault stays comfortably above the limit.
          </p>
          <Label>Amount of PLS</Label>
          <ControlRow style={{ marginTop: '0.4rem' }}>
            <Input type="number" value={collAmt} onChange={(e) => setCollAmt(e.target.value)}
              placeholder="0" step="1" min="0" />
            <Button type="button" variant="secondary" onClick={() => setCollAmt(maxSafeWithdraw().toFixed(6))}>
              Max safe
            </Button>
          </ControlRow>
          <Hint>Max safe withdrawal right now: {formatNumber(maxSafeWithdraw(), 0)} PLS</Hint>
          {/* Equal-weight buttons: these are alternatives, and mixed primary/secondary
              styling read as a required order (or as one being disabled). */}
          <ButtonRow>
            <Button disabled={!!busy || !collAmt || parseFloat(collAmt) <= 0}
              onClick={() => run(`Adding ${formatNumber(collAmt, 0)} PLS`, () => ev.addCollateral(toWad(collAmt))).then((ok) => ok && setCollAmt(''))}>
              Add collateral
            </Button>
            <Button disabled={!!busy || !collAmt || parseFloat(collAmt) <= 0}
              onClick={() => {
                if (parseFloat(collAmt) > maxSafeWithdraw() + 1e-6) {
                  toast.error(`Withdrawing more than ${formatNumber(maxSafeWithdraw(), 2)} PLS would put your vault too close to the limit.`);
                  return;
                }
                run(`Withdrawing ${formatNumber(collAmt, 0)} PLS`, () => ev.withdrawCollateral(toWad(collAmt))).then((ok) => ok && setCollAmt(''));
              }}>
              Withdraw to wallet
            </Button>
          </ButtonRow>
        </Section>
      )}
      </Card>
    </>
  );
};

export default EasyVault;
